// Shared revenue-recognition primitives for RevenueTicker and
// RevenueProjections. Both surfaces compute over the same row set
// (Analytics' analytics-field-jobs query) and must apply identical
// semantics — defining the resolvers and filters once here prevents the
// two from drifting (the scheduled-includes-completes fix that landed
// for the ticker would have had to be remembered in two places
// otherwise).
//
// Both timestamp resolvers are pure and return null when no usable
// timestamp exists (the consumer's accumulator skips the row).

import { parseISO, isValid } from 'date-fns'

// Completion-timestamp resolver. actual_end is the truth when present
// (stamped by the en_route→complete transition); scheduled_date is the
// next-best signal for jobs that completed on time; created_at is the
// last resort for backfilled rows. All three are populated on every
// complete job in dev, so this never returns null in practice.
export function completedAt(job: any): Date | null {
  const raw = job.actual_end || job.scheduled_date || job.created_at
  if (!raw) return null
  try {
    const d = typeof raw === 'string' ? parseISO(raw) : new Date(raw)
    return isValid(d) ? d : null
  } catch { return null }
}

// Scheduled-timestamp resolver — uses scheduled_date only. Scheduled
// revenue is forward-looking (what was booked); created_at would skew
// past buckets toward "when entered" rather than "when planned." A job
// with no scheduled_date isn't part of the schedule and shouldn't appear
// in scheduled rollups.
export function scheduledAt(job: any): Date | null {
  if (!job.scheduled_date) return null
  try {
    const d = typeof job.scheduled_date === 'string' ? parseISO(job.scheduled_date) : new Date(job.scheduled_date)
    return isValid(d) ? d : null
  } catch { return null }
}

// Recognized series filter — complete jobs only. Bucketed at
// completedAt(); each contributes its `total` to the recognized series.
export function isRecognized(job: any): boolean {
  return job.status === 'complete'
}

// Scheduled series filter — ALL non-cancelled jobs. Complete jobs are
// included intentionally so past scheduled buckets stay stable as work
// finishes; the gap between recognized and scheduled in past periods is
// the booked-vs-landed slippage view. Cancelled work is dropped (the
// booking evaporated).
export function isScheduled(job: any): boolean {
  return job.status !== 'cancelled'
}

// Convenience: the dollar value for a row. Both series sum the same
// field (the job's recognized gross), so projecting it once keeps the
// two series symmetrical.
export function jobValue(job: any): number {
  return Number(job.total) || 0
}
